import { eventsRequest } from '../../utils/eventsRequest';
import { OrderPaymentType, type CompletedSaleSnapshot, type CompletedSaleRegistration } from '../../types';

/** API response shape for GET /orders/:uuid */
interface OrderDetailsApiResponse {
  statusCode?: number;
  isSuccess?: boolean;
  data?: {
    uuid?: string;
    status?: number;
    depositTotal?: string | number;
    total?: string | number;
    paidInFullDiscountTotal?: string | number;
    paymentSummary?: {
      orderPaymentType?: OrderPaymentType;
      initialTotal?: string | number;
      couponDiscountTotal?: string | number;
      paidInFullDiscountTotal?: string | number;
      dueTodayAfterDiscounts?: string | number;
      depositPaidToday?: string | number;
      remainingBalance?: string | number;
      processingFee?: {
        baseAmount?: string | number;
        percentage?: string | number;
        fixedAmount?: string | number;
        amount?: string | number;
        description?: string;
      };
      totalDueToday?: string | number;
      totalPaidTodayWithFee?: string | number;
      totalPurchaseAmount?: string | number;
      chargedOn?: string;
    };
    createdAt?: string;
    updatedAt?: string;
    items?: OrderItemApi[];
    coupons?: unknown[];
  };
}

interface OrderItemApi {
  id?: number;
  amount?: string | number;
  depositAmount?: string | number | null;
  item?: {
    event?: { name?: string; startDate?: string; endDate?: string };
    autoPaymentDate?: string;
  };
  child?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    startDate?: string;
  };
}

function mapOrderItemToRegistration(
  item: OrderItemApi,
  orderPaymentType: OrderPaymentType
): CompletedSaleRegistration {
  const event = item.item?.event;
  const child = item.child;
  const itemPriceNum = Number(item.amount ?? 0);
  const depositAmountNum =
    item.depositAmount != null && String(item.depositAmount) !== '' ? Number(item.depositAmount) : 0;
  const isDepositPayment = orderPaymentType === OrderPaymentType.DEPOSIT;
  const paidTodayNum = isDepositPayment && depositAmountNum > 0 ? depositAmountNum : itemPriceNum;
  const remainingForItemNum =
    isDepositPayment && depositAmountNum > 0 ? Math.max(0, itemPriceNum - depositAmountNum) : 0;
  const amountPaid = String(paidTodayNum);
  const childName = child ? `${child.firstName ?? ''} ${child.lastName ?? ''}`.trim() : '';

  return {
    eventName: event?.name ?? '',
    childName,
    childId: child?.id,
    itemPrice: String(itemPriceNum),
    paidToday: String(paidTodayNum),
    remainingForItem: remainingForItemNum > 0 ? String(remainingForItemNum) : undefined,
    amountPaid,
    eventStartDate: event?.startDate ?? '',
    eventEndDate: event?.endDate ?? '',
    autoPaymentDate: isDepositPayment ? item.item?.autoPaymentDate : undefined,
  };
}

function mapApiResponseToSnapshot(api: OrderDetailsApiResponse): CompletedSaleSnapshot | null {
  const payload = api?.data;
  const items = payload?.items ?? [];
  const summary = payload?.paymentSummary;

  // Backend is now source-of-truth for payment math via paymentSummary.
  if (!payload || !summary || items.length === 0) return null;

  const orderPaymentType = summary.orderPaymentType ?? OrderPaymentType.FULL;
  const registrations = items.map((item) => mapOrderItemToRegistration(item, orderPaymentType));
  const autoPaymentDates = registrations.map((r) => r.autoPaymentDate).filter(Boolean) as string[];
  const scheduledAutoPaymentDate =
    orderPaymentType === OrderPaymentType.DEPOSIT && autoPaymentDates.length > 0
      ? autoPaymentDates.sort()[0]
      : undefined;

  const processingFeeAmount = Number(summary.processingFee?.amount ?? 0);
  const processingFeeName = summary.processingFee?.description ?? '';
  const initialTotal = Number(summary.initialTotal ?? 0);
  const couponDiscountTotal = Number(summary.couponDiscountTotal ?? 0);
  const payInFullDiscount = Number(summary.paidInFullDiscountTotal ?? 0);
  const dueTodayAfterDiscounts = Number(summary.dueTodayAfterDiscounts ?? 0);
  const remainingBalance = Number(summary.remainingBalance ?? 0);
  const depositPaidToday = Number(summary.depositPaidToday ?? 0);
  const totalPurchaseAmount = Number(summary.totalPurchaseAmount ?? 0);

  return {
    orderType: orderPaymentType,
    totalPaidToday: Math.max(0, dueTodayAfterDiscounts),
    depositPaid: depositPaidToday > 0 ? depositPaidToday : undefined,
    remainingBalance: Math.max(0, remainingBalance),
    totalPurchaseAmount: Math.max(0, totalPurchaseAmount),
    totalBeforeCoupon: initialTotal > 0 ? initialTotal : undefined,
    dueTodayBeforeCoupon: undefined,
    couponDiscountTotal: couponDiscountTotal > 0 ? couponDiscountTotal : undefined,
    processingFee: Math.max(0, processingFeeAmount),
    processingFeeName,
    payInFullDiscount: payInFullDiscount > 0 ? payInFullDiscount : undefined,
    registrations,
    scheduledAutoPaymentDate,
    hasOutstandingForms: false,
    depositTotal: undefined,
  };
}

/**
 * Fetches order details from GET /orders/:uuid and maps to CompletedSaleSnapshot.
 */
export async function getOrderDetails(
  orderUuid: string
): Promise<{ success: boolean; snapshot: CompletedSaleSnapshot | null; error?: string }> {
  try {
    const response = await eventsRequest<OrderDetailsApiResponse>(`/orders/${orderUuid}`, 'get');
    const snapshot = mapApiResponseToSnapshot(response.data);

    if (!snapshot || snapshot.registrations.length === 0) {
      return { success: false, snapshot: null, error: 'Order data could not be loaded.' };
    }

    return { success: true, snapshot };
  } catch (err) {
    console.error('Failed to fetch order details', err);
    return {
      success: false,
      snapshot: null,
      error: err instanceof Error ? err.message : 'Failed to load order details.',
    };
  }
}
